import { useEffect, useState } from 'react';

import { ApiError, getPurchaseStatus, type PurchaseStatus } from '../../lib/api.js';
import { setupAccount } from '../../lib/session.js';
import { PasswordInput } from '../../components/PasswordInput.js';
import './begin.css';

/**
 * The screen a client returns to from Stripe.
 *
 * It polls the purchase status — the payment is marked paid by the webhook, server to server, never
 * by this redirect, which anyone could visit. Once paid, and only then, it offers account setup:
 * the person chooses a password, the server creates the account **for the paid purchase's email**,
 * and they are signed straight in and taken to their reflection.
 *
 * So an account exists only after payment, only for the address that paid, and the person never has
 * to sign in as a separate step.
 */

const POLL_INTERVAL_MS = 2000;
const GIVE_UP_AFTER_MS = 40000;

export function Complete({ purchaseId }: { purchaseId: string }) {
  const [status, setStatus] = useState<PurchaseStatus['status'] | 'unknown'>('unknown');
  const [timedOut, setTimedOut] = useState(false);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the email already has a finished account — the way forward is to sign in, not set up.
  const [alreadyHasAccount, setAlreadyHasAccount] = useState(false);

  useEffect(() => {
    let alive = true;
    const startedAt = Date.now();

    const poll = async () => {
      try {
        const result = await getPurchaseStatus(purchaseId);
        if (!alive) return;
        setStatus(result.status);
        if (result.status === 'paid') return;
      } catch {
        // A transient read failure is not a payment failure; keep polling until the deadline.
      }
      if (!alive) return;
      if (Date.now() - startedAt > GIVE_UP_AFTER_MS) {
        setTimedOut(true);
        return;
      }
      window.setTimeout(() => void poll(), POLL_INTERVAL_MS);
    };

    void poll();
    return () => {
      alive = false;
    };
  }, [purchaseId]);

  const setUp = async () => {
    setBusy(true);
    setError(null);
    try {
      await setupAccount(purchaseId, fullName.trim(), password);
      // Signed in already — go straight to the reflection.
      window.location.assign('/');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'CONFLICT') {
        setAlreadyHasAccount(true);
        setError('This email already has an account. Please sign in with your existing password.');
      } else if (err instanceof ApiError && err.code === 'PAYMENT_REQUIRED') {
        setError('Your payment is still being confirmed. Please wait a moment and try again.');
      } else if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
        setError(
          'That password does not meet the requirements: at least 12 characters, with an upper and lower case letter and a number.',
        );
      } else {
        setError('We could not set up your account. Please try again in a moment.');
      }
      setBusy(false);
    }
  };

  if (status === 'paid') {
    return (
      <div className="begin__centered">
        <p className="begin__eyebrow">Payment received</p>
        <h1 className="complete__title">Set up your account</h1>
        <p className="complete__body">
          Choose a password. You will use it with your email to sign in and complete your reflection
          whenever you are ready.
        </p>

        <form
          className="login"
          onSubmit={(event) => {
            event.preventDefault();
            void setUp();
          }}
        >
          <label className="begin__field">
            <span className="begin__label">Your name</span>
            <input
              className="begin__input"
              value={fullName}
              autoComplete="name"
              onChange={(event) => setFullName(event.target.value)}
            />
          </label>

          <label className="begin__field">
            <span className="begin__label">Choose a password</span>
            <PasswordInput value={password} autoComplete="new-password" onChange={setPassword} />
            <span className="begin__hint">
              At least 12 characters, with an upper and lower case letter and a number.
            </span>
          </label>

          {error ? <p className="begin__error">{error}</p> : null}

          {alreadyHasAccount ? (
            <a className="button button--primary begin__submit" href="/login">
              Sign in instead
            </a>
          ) : (
            <button
              type="submit"
              className="button button--primary begin__submit"
              disabled={busy || fullName.trim() === '' || password.length < 12}
            >
              {busy ? 'Setting up…' : 'Create account and begin'}
            </button>
          )}
        </form>
      </div>
    );
  }

  if (timedOut) {
    return (
      <div className="complete">
        <h1 className="complete__title">This is taking a moment.</h1>
        <p className="complete__body">
          Your payment may still be going through. If you were charged, please refresh this page in a
          moment to set up your account — there is nothing else you need to do.
        </p>
      </div>
    );
  }

  return (
    <div className="complete">
      <h1 className="complete__title">Confirming your payment…</h1>
      <p className="complete__body">This takes a few seconds. Please do not close this window.</p>
    </div>
  );
}
